<template>
  <div>
    <b-card>
      <h2 v-if="!readonly" v-translate>Search and Delete</h2>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Quote Name</label>
            <input id="quote-name" v-model.lazy="filter.quoteName" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-region-from" v-translate>Region From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteRegionFrom" :options="regionsSelect">
              <template #first>
                <option :value="null" v-translate>Select Region</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-region-to" v-translate>Region To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteRegionTo" :options="regionsSelect">
              <template #first>
                <option :value="null" v-translate>Select Region</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-shipping-type" v-translate>Shipping Type</label>
            <b-form-select id="quote-shipping-type" v-model="filter.quoteShippingType" :options="this.shippingTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.sizeTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4" v-if="!readonly">
          <div class="form-group">
            <label for="quote-size" v-translate>Imported Quotes CSV</label>
            <b-form-file
              v-model="fileImported"
              :placeholder="$gettext('Choose an Imported CSV or drop it here...')"
              :drop-placeholder="$gettext('Drop file here...')"
              @input="handleFileImportedSelect"
              ref="importedQuotes"
              id="importedQuotes"
              accept=".csv"
            ></b-form-file>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Company</label>
            <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="filter.quoteCompanies.length === 0" />
            <div v-for="(item, index) in filter.quoteCompanies" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeCompanyFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <label for="quote-size">&nbsp;</label>
          <b-form-row>
            <b-col>
              <b-button size="sm" block variant="success" @click="searchQuotes" class="mb-2" :disabled="working">
                <b-spinner small v-if="working" />
                <translate>Search</translate>
              </b-button>
            </b-col>
            <b-col>
              <b-button size="sm" block variant="danger" @click="resetQuoteSearch" class="mb-2" :disabled="working">
                <translate>Reset Search</translate>
              </b-button>
            </b-col>
          </b-form-row>
        </b-col>
      </b-row>
      <div v-if="searchedQuotes.length && !readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="primary" @click="backupSelected" :disabled="selected.length === 0 || deleting" v-translate>
          Backup Selected
        </b-button>
        <b-button variant="warning" v-b-modal.modalEditFormulas :disabled="!backuped || deleting" v-translate>
          EDIT Selected
        </b-button>
        <b-button variant="danger" @click="deleteSelected" :disabled="!backuped || deleting" v-translate>
          DELETE Selected
        </b-button>
      </div>
      <div v-if="searchedQuotes.length && readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="warning" v-b-modal.modalEditFormulas v-translate>
          VIEW Selected
        </b-button>
      </div>
      <b-modal id="modalEditFormulas" size="xl" ref="modalEditFormulas" hide-footer @hidden="resetEditFormulas">
        <div slot="modal-title" v-translate>Bulk Edit Formulas</div>
        <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <b-row v-if="updating">
          <b-col>
            <b-progress :max="totalToUpdate.length" height="2rem" class="mb-3">
              <b-progress-bar :value="updatedCount">
                <span v-translate="{count: updatedCount, total: totalToUpdate.length}">Progress: <strong>%{count} / %{total}</strong></span>
              </b-progress-bar>
            </b-progress>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col>
            <h4 v-translate>Search &amp; Replace in names</h4>
            <b-row>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="search-for" v-translate>Search for:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="search-for" v-model="searchFor"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="replace-with" v-translate>Replace with:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="replace-with" v-model="replaceWith"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-button @click="bulkReplaceInQuoteNames" :disabled="updating" v-translate>Replace</b-button>
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <h4 v-translate>Formulas</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="formula-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="formula-by-percentage" v-model="formulaByPercentage" :disabled="formulasIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="formulasIsNan" v-model="formulasAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(formula, i) in distinctFormulas" :key="'formula' + i">
              <b-col sm="5">
                <label :for="`formula-${i}`">{{ formula }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`formula-${i}`" v-model="newFormulas[formula]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-button @click="bulkUpdateFormulas" :disabled="updating" v-if="!readonly" v-translate>Update</b-button>
      </b-modal>
      <bulk-delete-progress v-bind="bulkDeleteProps" v-on="bulkDeleteListeners" />
      <p v-if="total" v-translate="{total: this.total}">Total: <strong>%{total}</strong></p>
      <b-table id="quoteProvidersTable" ref="quoteProvidersTable" @row-selected="rowSelected" :busy.sync="working" selectable stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields" v-if="searchedQuotes.length > 0 && !deleting">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(reference)="data">
          <a :href="'/edit/quote-providers/' + data.item.id" target="_blank" v-if="!readonly">{{data.item.reference}}</a>
          <span v-else>{{data.item.reference}}</span>
        </template>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? sizeTypes.findIndex(x => x.id === data.item.size_id) !== -1 ? sizeTypes.find(x => x.id === data.item.size_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.findIndex(x => x.id === data.item.area_id_from) !== -1 ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.findIndex(x => x.id === data.item.area_id_to) !== -1 ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(region_id_from)="data">
          {{ data.item.region_id_from ? regions.findIndex(x => x.id === data.item.region_id_from) !== -1 ? regions.find(x => x.id === data.item.region_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(region_id_to)="data">
          {{ data.item.region_id_to ? regions.findIndex(x => x.id === data.item.region_id_to) !== -1 ? regions.find(x => x.id === data.item.region_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.findIndex(x => x.id === data.item.shipping_type_id) !== -1 ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(company_id)="row">
          <strong class="badge" :style="`background-color: ${stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id)};${textContrastColor(stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id))}`">{{row.item.company.name || row.item.company.id}}</strong>
        </template>
      </b-table>
    </b-card>
    <b-card v-if="!readonly">
      <h2 v-translate>Import</h2>
      <b-form-file
        v-model="file"
        :placeholder="$gettext('Choose a file or drop it here...')"
        :drop-placeholder="$gettext('Drop file here...')"
        @input="handleFileSelect"
        ref="csvToImport"
        id="csvToImport"
        accept=".csv"
      ></b-form-file>
      <b-alert variant="success" :show="importing" class="mt-3">
        <span v-translate="{count: count, total: importData.length}">Importing %{count}/%{total}</span>
      </b-alert>
      <b-alert variant="info" :show="importData.length > 0 && errors.length === 0" class="mt-3">
        <h5 v-translate="{total: importData.length}">It seems there are no errors and you are about to bulk import %{total} quotes. Is it OK?</h5>
        <b-button variant="success" @click="bulkImport" :disabled="importing">
          <b-spinner small v-show="importing" />
          <translate>OK! Let's import them!</translate>
        </b-button>
        <b-button variant="danger" @click="file = null; importData = []" :disabled="importing" v-translate>
          NOPE! Cancel it!
        </b-button>
      </b-alert>
      <b-alert variant="danger" :show="errors.length > 0" class="mt-3">
        <h5 v-translate>It seems there are errors, check 'em out:</h5>
        <ul>
          <li v-for="(item, index) in errors" :key="index">
            {{ item.message }}{{item.row ? ' (row: ' + item.row + (item.index ? ' - col: ' + item.index : '') + ')' : ''}}
          </li>
        </ul>
        <p v-translate>Please, review and fix your CSV file and upload it again</p>
      </b-alert>
    </b-card>
  </div>
</template>
<script>
import { getCountry } from '@/services/country'
import { toFiltersParam } from '@/common/apiFilters'

  import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
  import BulkDeleteProgress from '@/components/BulkDeleteProgress'
  import bulkDeletePool from '@/mixins/bulkDeletePool.mixin'
  import Papa from 'papaparse'

  export default {
    name: 'BulkQuoteProvidersList',
    components: {
      CompanyAutocomplete,
      BulkDeleteProgress
    },
    mixins: [bulkDeletePool],
    props: {
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        file: null,
        fileImported: null,
        importData: [],
        errors: [],
        quotesFields: [
          {key: 'reference', label: this.$gettext('Name')},
          {key: 'company_id', label: this.$gettext('Provider')},
          {key: 'area_id_from', label: this.$gettext('Area From')},
          {key: 'area_id_to', label: this.$gettext('Area To')},
          {key: 'region_id_from', label: this.$gettext('Region From')},
          {key: 'region_id_to', label: this.$gettext('Region To')},
          {key: 'size_id', label: this.$gettext('Size')},
          {key: 'shipping_type_id', label: this.$gettext('Shipping Type')},
          {key: 'formula', label: this.$gettext('Formula')},
          {key: 'date_from', label: this.$gettext('From Date')},
          {key: 'date_to', label: this.$gettext('To Date')}
        ],
        filters: [],
        filter: {
          ids: [],
          quoteName: '',
          quoteIncludes: null,
          quoteAreaFrom: null,
          quoteAreaTo: null,
          quoteRegionFrom: null,
          quoteRegionTo: null,
          quoteCompanies: [],
          quoteShippingType: '',
          quoteShippingSizeType: ''
        },
        working: false,
        searchedQuotes: [],
        companiesQuotes: [],
        companyAcOptions: {
          debounce: 500,
          placeholder: this.$gettext('Company Name, Business Name or TIN'),
          inputClass: 'form-control'
        },
        searchCompanyQuery: '',
        minCharacter: 3,
        areas: [],
        platforms: [],
        platformsSelect: [],
        shippingTypes: [],
        shippingTypesSelect: [],
        sizeTypes: [],
        sizeTypesSelect: [],
        selected: [],
        selectAllTrigger: false,
        backuped: false,
        importing: false,
        // `count` belongs to bulkImport only; the delete flow keeps its own state
        // in bulkDeletePool.mixin so both progress bars stay independent
        count: 0,
        // one step per row: unlike client quotes there is no reverse-creation chain
        deletePhaseKeys: ['quoteProviders'],
        deletePhaseLabels: {quoteProviders: this.$gettext('quote-providers')},
        deleteRowLabelField: 'reference',
        deleteErrorCsvIdColumn: 'quote_provider_id',
        deleteErrorsCsvName: 'quote_providers',
        batchUpdateChunk: 15,
        total: null,
        updating: false,
        totalToUpdate: 0,
        updatedCount: 0,
        formulaByPercentage: null,
        newFormulas: {},
        formulasAsIntegers: false,
        searchFor: null,
        replaceWith: null
      }
    },
    computed: {
      distinctFormulas() {
        return [...new Set(this.selected.map(row => row.formula))]
      },
      formulasIsNan() {
        return !!this.distinctFormulas.filter(x => isNaN(x))[0]
      }
    },
    watch: {
      formulaByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctFormulas.length; i++) {
            this.newFormulas[this.distinctFormulas[i]] = parseFloat(this.distinctFormulas[i]) + parseFloat(this.distinctFormulas[i]) * val / 100
          }
        } else {
          this.newFormulas = {}
        }
      },
      'filter.ids': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteIds = [ 'id', 'in', val ]
            } else {
              this.filters.quoteIds = []
            }
          }
        },
        deep: true
      },
      'filter.quoteName': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteName = [ 'reference', 'ct', val ]
            } else {
              this.filters.quoteName = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteAreaFrom = [ 'area_id_from', 'eq', val ]
            } else {
              this.filters.quoteAreaFrom = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteAreaTo = [ 'area_id_to', 'eq', val ]
            } else {
              this.filters.quoteAreaTo = []
            }
          }
        },
        deep: true
      },
      'filter.quoteRegionFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteRegionFrom = [ 'region_id_from', 'eq', val ]
            } else {
              this.filters.quoteRegionFrom = []
            }
          }
        },
        deep: true
      },
      'filter.quoteRegionTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteRegionTo = [ 'region_id_to', 'eq', val ]
            } else {
              this.filters.quoteRegionTo = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteShippingType = [ 'shipping_type_id', 'eq', val ]
            } else {
              this.filters.quoteShippingType = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingSizeType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteShippingSizeType = [ 'size_id', 'eq', val ]
            } else {
              this.filters.quoteShippingSizeType = []
            }
          }
        },
        deep: true
      },
      'filter.quoteCompanies': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              const companies = val.map(function (item) {
              return item.id
            })
              this.filters.quoteCompanies = [ 'company_id', 'in', companies ]
            } else {
              this.filters.quoteCompanies = []
            }
          }
        },
        deep: true
      },
      selectAllTrigger(val) {
        if (val === true) {
          this.selectAll()
        } else {
          this.unselectAll()
        }
      },
      selected(val) {
      }
    },
    created() {
      this.fetchAreas()
      this.fetchRegions()
      this.$http.get(this.$appConfig.api.baseUrl + 'shipping-types')
        .then(response => {
          this.shippingTypes = response.body.data
          this.shippingTypesSelect = this.shippingTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.shippingTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific type')
          })
          this.shippingTypes.unshift({
            id: '',
            name: this.$gettext('All types')
          })
        })
      this.$http.get(this.$appConfig.api.baseUrl + 'sizes')
        .then(response => {
          this.sizeTypes = response.body.data
          this.sizeTypesSelect = this.sizeTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.sizeTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific size')
          })
          this.sizeTypes.unshift({
            id: '',
            name: this.$gettext('All sizes')
          })
          for (let i = 0; i < this.sizeTypesSelect.length; i++) {
            for (let n = 0; n < this.companyFormulas.length; n++) {
              if (this.companyFormulas[n].size_type_id === this.sizeTypesSelect[i].value) {
                this.sizeTypesSelect.splice(i, 1)
              }
            }
          }
        }).catch(response => {

        })
    },
    methods: {
      resetEditFormulas() {
        this.newFormulas = {}
        this.formulaByPercentage = null
        this.searchFor = null
        this.replaceWith = null
      },
      async bulkReplaceInQuoteNames() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => x.reference.indexOf(this.searchFor) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {

          const payload = {}
          if (this.selected[i].reference.indexOf(this.searchFor) !== -1) {
            payload.reference = this.selected[i].reference.replace(this.searchFor, this.replaceWith)
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quote-providers/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.filter.quoteName = this.replaceWith
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      async bulkUpdateFormulas() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => Object.keys(this.newFormulas).indexOf(x.formula) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          const payload = {}
          if (this.newFormulas[this.selected[i].formula]) {
            payload.formula = this.newFormulas[this.selected[i].formula]
            if (this.formulasAsIntegers && !isNaN(payload.formula)) {
              payload.formula = parseInt(payload.formula)
            }
            // the API validates formula as a string; the percentage watcher and parseInt both yield numbers
            payload.formula = String(payload.formula)
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quote-providers/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      handleFileImportedSelect(evt) {
        const file = this.fileImported
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: function (results) {
              _this.filter.ids = results.data.map(id => { return id.quote_id })
            }
          })
        }
      },
      handleFileSelect(evt) {
        const file = this.file
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: function (results) {
              _this.errors = []
              _this.importData = []
              if (results.errors.length === 0) {
                if (
                  results.meta.fields.indexOf('reference') !== -1 &&
                  (results.meta.fields.indexOf('area_id_from') !== -1 &&
                  results.meta.fields.indexOf('area_id_to') !== -1) ||
                  (results.meta.fields.indexOf('region_id_from') !== -1 &&
                  results.meta.fields.indexOf('region_id_to') !== -1) &&
                  results.meta.fields.indexOf('company_id') !== -1 &&
                  results.meta.fields.indexOf('formula') !== -1
                ) {
                  _this.importData = results.data
                  _this.validateFormulaCommas(results.data)
                  _this.validateCatalogIds(results.data)
                  // console.log((_this.importData[1].companies + '').split(','))
                } else {
                  _this.errors = [
                    {
                      message: _this.$gettext('There are one or more missing required data columns')
                    }
                  ]
                }
              } else {
                _this.errors = results.errors
              }
            }
          })
        }
      },
      validateCatalogIds(rows) {
        const catalogFields = [
          // areas and regions are fetched scoped to the active country, the rest are global
          {field: 'area_id_from', catalog: this.areas, countryScoped: true},
          {field: 'area_id_to', catalog: this.areas, countryScoped: true},
          {field: 'region_id_from', catalog: this.regions, countryScoped: true},
          {field: 'region_id_to', catalog: this.regions, countryScoped: true},
          {field: 'size_id', catalog: this.sizeTypes},
          {field: 'shipping_type_id', catalog: this.shippingTypes}
        ]
        // best-effort: catalogs still loading are skipped, the backend remains the authority
        const checks = catalogFields
          .filter(x => { return x.catalog && x.catalog.length > 0 })
          .map(x => {
            return {
              field: x.field,
              countryScoped: x.countryScoped,
              // 'null' and '' are the "All"/"No specific" sentinel entries, not real ids
              ids: new Set(x.catalog.map(item => { return item.id }).filter(id => { return id !== 'null' && id !== '' }))
            }
          })
        for (let i = 0; i < rows.length; i++) {
          for (const check of checks) {
            const value = rows[i][check.field]
            if (value !== null && value !== undefined && value !== '' && !check.ids.has(value)) {
              this.errors.push({
                message: this.$gettextInterpolate(
                  // an id from another country exists, it is just missing from the
                  // country-scoped catalog — saying so tells the operator what to clean
                  check.countryScoped
                    ? this.$gettext("Id %{value} in '%{field}' does not exist in the active country")
                    : this.$gettext("Id %{value} in '%{field}' does not exist"),
                  {value, field: check.field}
                ),
                row: i + 1
              })
            }
          }
        }
      },
      validateFormulaCommas(rows) {
        const field = 'formula'
        for (let i = 0; i < rows.length; i++) {
          const value = rows[i][field]
          if (value !== null && value !== undefined && (value + '').replace(/\{[^{}]*\}/g, '').includes(',')) {
            this.errors.push({
              message: this.$gettextInterpolate(
                this.$gettext("Decimal comma detected in '%{field}' — decimals must use a dot"),
                {field}
              ),
              row: i + 1
            })
          }
        }
      },
      async bulkImport() {
        this.importing = true
        this.count = 0
        const created = []
        for (let i = 0; i < this.importData.length; i += this.batchUpdateChunk) {
          const chunk = this.importData.slice(i, i + this.batchUpdateChunk)
          // Promise.allSettled is unavailable with the project's babel runtime (core-js 2)
          const results = await Promise.all(chunk.map((row, j) => {
            return this.importQuoteProviderRow(row, i + j + 1)
              .then(value => { return {status: 'fulfilled', value} })
              .catch(reason => { return {status: 'rejected', reason} })
          }))
          for (const result of results) {
            if (result.status === 'fulfilled') {
              created.push(result.value)
              this.count++
            } else {
              this.errors.push({message: result.reason.message, row: result.reason.row})
            }
          }
        }
        this.file = null
        this.importData = []
        this.importing = false
        if (created.length > 0) {
          const ids = created.map(x => { return {quote_id: x.quoteId} })
          this.exportToCsv('imported_quote_providers__' + created[created.length - 1].createdAt + '.csv', Papa.unparse(ids))
          // list by the exact created ids — a created_at window misses rows because chunked rows don't finish in chronological order
          const createdIds = created.map(x => { return x.quoteId })
          this.filter.ids = createdIds
          this.filters.quoteIds = ['id', 'in', createdIds]
          await this.searchImportedQuotes(createdIds)
        } else {
          this.searchQuotes()
        }
      },
      async importQuoteProviderRow(row, rowNumber) {
        const autodiscover = this.customValuesAutodiscover(row.formula)
        const cleanedUpData = JSON.parse(JSON.stringify(row).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))
        const quote = {...cleanedUpData, ...autodiscover}
        const paramsQuotes = {
          reference: quote.reference,
          country_iso_code: quote.country_iso_code,
          area_id_from: quote.area_id_from || null,
          area_id_to: quote.area_id_to || null,
          region_id_from: quote.region_id_from || null,
          region_id_to: quote.region_id_to || null,
          size_id: quote.size_id,
          shipping_type_id: quote.shipping_type_id || null,
          company_id: quote.company_id,
          formula: quote.formula,
          formula_params: quote.formula_params,
          date_from: quote.date_from,
          date_to: quote.date_to
        }
        if (quote.area_id_from && quote.area_id_to) {
          delete paramsQuotes.region_id_from
          delete paramsQuotes.region_id_to
        } else if (quote.region_id_from && quote.region_id_to) {
          delete paramsQuotes.area_id_from
          delete paramsQuotes.area_id_to
        }
        try {
          // a row is a single POST, so there is no partial state to roll back
          const response = await this.$http.post(this.$appConfig.api.baseUrl + 'quote-providers', paramsQuotes)
          return {quoteId: response.body.id, createdAt: response.body.created_at}
        } catch (originalError) {
          const error = new Error(
            (originalError && originalError.body && originalError.body.message) ||
            (originalError && originalError.message) ||
            this.$gettext('Import request failed')
          )
          error.row = rowNumber
          throw error
        }
      },
      customValuesAutodiscover(formulas) {
        const excludeValues = [
          'kgms'
        ]
        const regexp = /\{([^}]+)\}/g
        const valuesArray = []
        const paramsArray = []
        let match = regexp.exec(formulas)
        while (match !== null) {
          if (!excludeValues.includes(match[1])) {
            valuesArray.push(match[1])
          }
          if (!excludeValues.includes(match[1])) {
            paramsArray.push(match[1])
          }
          match = regexp.exec(formulas)
        }
        if (paramsArray.length) {
          return {
            formula_params: Object.fromEntries(paramsArray.map(x => x.split(':')))
          }
        } else {
          return null
        }
      },
      async searchQuotes() {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        const params = {
        page: 0,
        limit: 500,
        sort: [
          {
            key: 'reference',
            direction: 'asc'
          }
        ],
        includes: [
          'company',
          'area_from',
          'area_to'
        ],
        filter_groups: []
      }
        if (!this.$cookies.get('bypassCountry')) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models['quote-providers'].countrize, 'eq', getCountry()]
              ]
            }
          )
        }

        if (this.filters) {
          params.filter_groups.push(
            {filters: Object.values(this.filters)}
          )
        }
        params.filters = toFiltersParam(params.filter_groups)
        delete params.filter_groups
        await this.doSearchQuotes(params)
        this.working = false
      },
      async searchImportedQuotes(createdIds) {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        this.searchedQuotes = []
        // the created ids are the only filter needed, sent as a JSON string param
        // (the bracket-expanded querystring explodes URL limits) and in chunks,
        // because even as JSON the ids travel in the URL
        const chunkSize = 250
        for (let i = 0; i < createdIds.length; i += chunkSize) {
          const params = {
            page: 0,
            limit: 500,
            sort: [
              {
                key: 'reference',
                direction: 'asc'
              }
            ],
            includes: [
              'company',
              'area_from',
              'area_to'
            ],
            filters: toFiltersParam([{filters: [['id', 'in', createdIds.slice(i, i + chunkSize)]]}])
          }
          await this.doSearchQuotes(params, this.searchedQuotes)
        }
        this.working = false
      },
      async doSearchQuotes(params, prevQuotes = []) {
        let searchedQuotes = []
        return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quote-providers'].endpoint, {params})
          .then(async response => {
            searchedQuotes = response.body.data

            searchedQuotes = prevQuotes.concat(searchedQuotes)
            if (response.body.data.length !== 0) {
              params.page = params.page + 1
              await this.doSearchQuotes(params, searchedQuotes)
            } else {
              this.total = searchedQuotes.length
              this.searchedQuotes = searchedQuotes
            }
          })
      },
      resetQuoteSearch() {
        this.total = null
        this.resetting = true
        this.searchedQuotes = []
        this.filters = []
        this.fileImported = null
        Object.assign(this.$data.filter, this.$options.data.call(this).filter)
        setTimeout(function () {
          this.resetting = false
        }.bind(this), 500)
      },
      rowSelected(items) {
        this.selected = items
      },
      selectAll() {
        // selectAllRows() selects in one pass; clicking every <tr> meant one
        // row-selected event per row, which froze the screen over 500 rows.
        // The ref guard matters because the watcher runs on nextTick, when the
        // table may already be out of the render tree (a delete run just started)
        if (this.$refs.quoteProvidersTable) {
          this.$refs.quoteProvidersTable.selectAllRows()
        }
      },
      unselectAll() {
        if (this.$refs.quoteProvidersTable) {
          this.$refs.quoteProvidersTable.clearSelected()
        }
      },
      onSearchCompanySelected(item) {
        this.filter.quoteCompanies.push({id: item.id, name: item.name})
        this.$refs.companyAc.searchQuery = ''
      },
      removeCompanyFromFilters(index) {
        this.filter.quoteCompanies.splice(index, 1)
      },
      backupSelected() {
        const quotesExport = []
        for (let i = 0; i < this.selected.length; i++) {
          const originalQuote = JSON.parse(JSON.stringify(this.selected[i]))

          try {
            const theQuote = originalQuote
            delete (theQuote.id)
            delete (theQuote.applied_by_id)
            delete (theQuote.created_at)
            delete (theQuote.updated_at)
            delete (theQuote.deleted_at)
            delete (theQuote.company)
            delete (theQuote.area_from)
            delete (theQuote.area_to)
            quotesExport.push(theQuote)
          } catch (e) {
            console.error(e)
          }
        }

        let filename = 'quote_providers_backup'
        Object.values(this.filters).forEach(filter => {
          if (filter.length > 0) {
            // summarize long array values (e.g. the post-import ids filter) so the
            // filename never exceeds filesystem name limits
            const parts = filter.map(part => {
              return Array.isArray(part) && part.length > 10 ? part.length + '-values' : part
            })
            filename += '__' + parts.join('-')
          }
        })
        filename += '.csv'
        this.exportToCsv(filename, Papa.unparse(quotesExport))
        this.backuped = true
      },
      // a provider quote has no formula rows to unlink, so one DELETE is the whole
      // chain — the pool, the retries and the 404 tolerance come from the mixin
      deleteStepsFor(row) {
        return [
          {
            name: 'quote-providers',
            counter: 'quoteProviders',
            run: () => { return this.deleteRequest('delete', 'quote-providers/' + row.id) }
          }
        ]
      },
      exportToCsv(filename, csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, filename)
        } else {
          const link = document.createElement('a')
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      }
    }
  }
</script>
